import type { SurfaceRenderer } from '../host.js';
import { makeIcon, type IconName } from '@caos/ui';
import { renderHero } from '../components/hero.js';
import { openPackagePicker } from '../install/package-picker.js';
import type { CatalogEntry, PackageKind } from '../install/catalog.js';
import { SESSION_SIGN_OUT_ENDPOINT, signOutSession } from '../session-sync.js';
import './empty-workspace.css';

/**
 * The empty-workspace surface — the `surface: "empty-workspace"` branch (CAOS-133).
 *
 * This is one of the kernel's two frozen pre-install surfaces: what a freshly stood-up dev org
 * shows once a session has booted but the capability registry resolved NO shell master. It is a
 * VALID empty state, not an error (how-it-works.md step 3) — "Engine online. Workspace standing
 * by." — and it must render with ONLY the frozen default design system, before any package
 * is installed.
 *
 * It EMBEDS the shared branded hero ({@link renderHero}, CAOS-131) with the `stack` motif — the
 * isometric metadata-stack schematic from `empty-workspace-mockup.png` — and mounts it full-
 * bleed (see empty-workspace.css). All copy is caller-owned here; the wordmark/OS-suffix and the
 * accent come from the brand-token seam, so a white-label swap ([data-brand=acme]) repaints
 * the wordmark AND the accent from one small token map — token-only styling, no literals.
 *
 * The call-to-action buttons come from the kernel's `decision.actions` ({@link BootActions}): the
 * first is the primary "Install workspace", the rest secondary. The surface owns the button
 * controls and hands them to the hero via {@link HeroProps.actions}, which appends them verbatim,
 * so the boot actions keep their behaviour while the hero owns only their placement. Each CTA
 * carries the mockup's leading glyph (CAOS-210): a download for the install action, a package for
 * the deploy action.
 */

/**
 * CAOS-602 — three marks this surface drew for itself, now the platform's own. The boot screen is
 * one of the two surfaces the Engine bakes in, which is exactly why it must not carry a private
 * icon set: it is the first thing anybody sees and the least likely place to notice a mark drifting
 * from the one every other surface uses. The lighter stroke is kept, because that is presentation
 * and this surface gets to decide it; the artwork is not.
 */
function glyph(name: IconName): SVGSVGElement {
  const svg = makeIcon(name);
  svg.setAttribute('stroke-width', '1.8');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  return svg;
}

/** Download — a down-arrow into a tray (the "install" CTA). */
function downloadIcon(): SVGSVGElement {
  return glyph('download');
}

/** Log-out — a door with an out-arrow (the sign-out control, CAOS-213). */
function logOutIcon(): SVGSVGElement {
  return glyph('log-out');
}

/** Package — a closed box (the "deploy an app" CTA). */
function packageIcon(): SVGSVGElement {
  return glyph('package');
}

/** The leading glyph for a boot action, keyed by its id (falls back to order). */
function iconFor(actionId: string, isPrimary: boolean): SVGSVGElement {
  if (actionId === 'deploy-app') return packageIcon();
  if (actionId === 'install-shell') return downloadIcon();
  return isPrimary ? downloadIcon() : packageIcon();
}

/** Build one CTA: a real `<caos-button>` with a leading glyph slotted before its label. */
function ctaButton(
  label: string,
  variant: 'primary' | 'secondary',
  icon: SVGSVGElement,
  onClick: () => void,
): HTMLElement {
  const b = document.createElement('caos-button');
  b.setAttribute('variant', variant);
  b.append(icon, document.createTextNode(label));
  b.addEventListener('click', onClick);
  return b;
}

/**
 * The empty-workspace behaviour seam (CAOS-211).
 *
 * The CTAs open the package picker; on a successful install the picker calls `reboot`, which
 * re-runs the boot decision so a now-present shell master flips this surface into the painted
 * workspace. Production wires `reboot` to a live re-fetch + re-render (see `main.ts`); the built-
 * in fallback is a full reload, which re-runs boot the honest way. `openPicker` is overridable so
 * the deterministic test drives the CTA wiring without the real overlay/install stack.
 */
export interface EmptyWorkspaceDeps {
  /** Re-run the boot decision and re-render — invoked after a successful install. */
  reboot: () => void | Promise<void>;
  /** Open the package picker for a CTA group. Overridable for tests. */
  openPicker: (group: PackageKind, onInstalled: () => void | Promise<void>) => void;
  /**
   * CAOS-1728 — which catalog the CTAs offer. Absent means {@link STANDARD_CATALOG}, which is what
   * every caller that does not mention it gets, so production is the default rather than a case
   * that has to be selected correctly.
   *
   * THIS IS THE "SELECTED EXPLICITLY" HALF OF THE REQUIREMENT, and it is a function argument on
   * purpose. The proving catalog holds throwaway fixtures, and the way scaffolding reaches a
   * customer is ambient selection — an env var read at module load, a `NODE_ENV` check, a hostname
   * sniff. None of those is wrong when it is written; one of them is wrong later, in the single
   * deployment nobody exercises, and it fails open. Requiring an argument means no RUNTIME
   * condition can turn this on by itself.
   *
   * What it does NOT do, stated because the argument is easy to overstate: this field cannot stop a
   * CALLER from passing the fixtures. Any module may call this function, so the guarantee that the
   * fixtures stay out of a customer build is the one in `main.ts` — the only caller that passes a
   * catalog gates it behind `import.meta.env.DEV`, a build-time constant, so the branch and the
   * import are eliminated from a production bundle. That is a property of the build, not of this
   * signature, and it is what the bundle check on this change actually measures.
   */
  catalog?: readonly CatalogEntry[];
}

const defaultDeps: EmptyWorkspaceDeps = {
  reboot: () => window.location.reload(),
  // `deps` rather than a captured `catalog`: this closure is built once, and reading the field at
  // call time is what lets a later `configureEmptyWorkspace({ catalog })` take effect without the
  // caller also having to replace `openPicker`. Passing the two separately is how a proving run
  // ends up with the fixture catalog and the production picker, or the reverse.
  openPicker: (group, onInstalled) => {
    openPackagePicker({ group, onInstalled, catalog: deps.catalog });
  },
};

let deps: EmptyWorkspaceDeps = defaultDeps;

/** Wire the empty-workspace behaviour seam (production reboot / dev fixture / tests). */
export function configureEmptyWorkspace(next: Partial<EmptyWorkspaceDeps>): void {
  deps = { ...deps, ...next };
}

/** Map a boot action id to the catalog group its CTA opens. */
function groupForAction(actionId: string): PackageKind {
  return actionId === 'deploy-app' ? 'app' : 'workspace';
}

/**
 * The sign-out control (CAOS-213).
 *
 * Once signed in, the empty-workspace landing is the whole surface — the shell (which owns the
 * header, nav, and avatar menu) is not installed yet, so there is no chrome to log out FROM. This
 * control fills that gap: a mono, unobtrusive affordance anchored to the hero's top-right corner,
 * styled from the same hero tokens (teal accent, hero ink) so it reads as part of the page, not
 * bolted on. It drives the EXISTING logout path — the host worker's `/api/auth/logout` proxy,
 * which clears the session cookie — then returns to `/`, where a now-sessionless boot resolves the
 * sign-in surface. The engine's logout is untouched; this is only the pixel affordance for it.
 */
/** The logout seam: the default hits the host logout proxy and re-boots. Overridable for tests. */
export type SignOutHandler = () => void | Promise<void>;

const defaultSignOut: SignOutHandler = async () => {
  try {
    await signOutSession(() =>
      fetch(SESSION_SIGN_OUT_ENDPOINT, { method: 'POST', credentials: 'same-origin' }),
    );
  } finally {
    // Cookie cleared by the host worker ⇒ the next boot resolves sign-in. Navigate even on a
    // network error: a stale session is a worse outcome than an extra sign-in prompt.
    window.location.assign('/');
  }
};

let signOut: SignOutHandler = defaultSignOut;

/** Override the logout seam (production wiring / tests). */
export function setSignOutHandler(fn: SignOutHandler): void {
  signOut = fn;
}

/** Build the top-right sign-out control: a mono button with a leading log-out glyph. */
function signOutControl(): HTMLElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'caos-empty__signout';
  b.setAttribute('aria-label', 'Sign out');
  b.append(logOutIcon(), document.createTextNode('Sign out'));
  b.addEventListener('click', () => {
    b.setAttribute('aria-disabled', 'true');
    void Promise.resolve(signOut());
  });
  return b;
}

/**
 * The empty-state copy, chosen from the kernel's `appsInstalled` hint (CAOS-205).
 *
 * Two valid empty states share this one surface:
 *   • nothing installed — the STANDING empty state: "Engine online. Workspace standing by."
 *   • an app installed, but no workspace — the app-aware state: the app is a metadata fact the
 *     Engine already holds; it just has no workspace to be seen in yet. This is where the
 *     app→workspace SOFT HINT surfaces (the dependency policy: an app never hard-depends on a
 *     workspace, so installing one is allowed and valid — it is simply not visible until a
 *     workspace exists). The primary CTA (kernel-supplied) points at installing a workspace.
 *
 * All copy stays caller-owned here; only which SET of copy applies is driven by the kernel fact.
 */
interface EmptyStateCopy {
  readonly eyebrow: readonly string[];
  readonly headline: string;
  readonly body: readonly string[];
}

function copyFor(appsInstalled: boolean): EmptyStateCopy {
  if (appsInstalled) {
    return {
      eyebrow: ['App installed', 'No workspace yet'],
      headline: 'App installed — add a workspace to open it.',
      body: [
        'Your CAOS environment is provisioned and an app is installed.',
        'An app needs a workspace to be seen — none is installed yet.',
        'Install a workspace, and CAOS opens your app inside it.',
      ],
    };
  }
  return {
    eyebrow: ['Provisioned and ready', 'No apps installed'],
    headline: 'Engine online. Workspace standing by.',
    body: [
      'Your CAOS environment is provisioned and the Engine is online.',
      'No workspace or apps are installed yet.',
      'Install a workspace or deploy an app, and CAOS brings your environment to life.',
    ],
  };
}

export const emptyWorkspaceSurface: SurfaceRenderer<'empty-workspace'> = {
  surface: 'empty-workspace',
  render(decision, ctx) {
    const controls = decision.actions.map((action, i) =>
      ctaButton(action.label, i === 0 ? 'primary' : 'secondary', iconFor(action.id, i === 0), () =>
        deps.openPicker(groupForAction(action.id), () => deps.reboot()),
      ),
    );

    const copy = copyFor(decision.appsInstalled);
    const hero = renderHero({
      motif: 'stack',
      eyebrow: copy.eyebrow,
      headline: copy.headline,
      body: copy.body,
      actions: controls,
      footer: ['CAOS ENGINE ONLINE', 'VERSION 1.0.0', 'WHITE-LABEL READY'],
    });

    // A positioning frame so the sign-out control (CAOS-213) can anchor to the hero's corner
    // without disturbing the hero's own full-bleed layout.
    const frame = document.createElement('div');
    frame.className = 'caos-empty';
    frame.append(hero, signOutControl());
    ctx.mount.append(frame);
  },
};
